import {Component, effect, inject, input, signal, WritableSignal} from '@angular/core';
import {MatFormField, MatHint, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ExtendedFieldQuery, ExtendedFieldQueryGroup} from 'src/app/core/definitions/extended-search-params';
import {CdkConnectedOverlay, CdkOverlayOrigin} from '@angular/cdk/overlay';
import {ExtendedSearchService} from '../../../../core/extended-search.service';
import {SearchContainer} from '../../../../core/definitions/search-container';
import {
  ExtendedSearchQueryBuilderValueDisplayComponent
} from '../../extended-search-query-builder-value-display/extended-search-query-builder-value-display.component';
import {SearchObject} from '../../../../core/definitions/search-object';
import {FieldInputType} from "../../../../core/definitions/field-input-type.enum";
import {resolveFieldValueOnLeave, visibleNameWithoutInvalidMarkers} from './extended-search-field-value-leave.calculator';

@Component({
  selector: 'app-extended-search-query-builder-field-value',
  imports: [
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatLabel,
    MatSuffix,
    TranslateModule,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    ExtendedSearchQueryBuilderValueDisplayComponent
  ],
  templateUrl: './extended-search-query-builder-field-value.component.html',
  styleUrl: './extended-search-query-builder-field-value.component.scss'
})
export class ExtendedSearchQueryBuilderFieldValueComponent {
  // Inject
  private readonly extendedSearchService: ExtendedSearchService = inject(ExtendedSearchService);
  private readonly translate: TranslateService = inject(TranslateService);

  dropdownOpen: boolean = false;
  unselectedLiteralHint = false;
  readonly valueInputId = `query-builder-value-input-${crypto.randomUUID()}`;

  disabled = input<boolean>(false);
  parentGroup = input.required<ExtendedFieldQueryGroup>();
  rootGroup = input.required<ExtendedFieldQueryGroup>();
  searchContainer = input.required<SearchContainer>();
  selfField = input.required<ExtendedFieldQuery>();

  expandValueNodes: WritableSignal<boolean> = signal<boolean>(false);
  filterInputDebounce = null;
  filterQuery: WritableSignal<string> = signal<string>('');
  values: WritableSignal<SearchObject[]> = signal<SearchObject[]>([]);

  constructor() {
    effect(() => {
      if (this.hasDropdown) {
        if (this.filterQuery() === '') {
          this.expandValueNodes.set(false);
        } else {
          this.expandValueNodes.set(true);
        }
      }
    });

    effect(() => {
      if (this.hasDropdown && !this.dropdownOpen) {
        this.getValueSelectorOptions();
      }
    });

    effect(() => {
      if (this.filterQuery() === '') {
        this.getValueSelectorOptions();
      } else {
        this.getValueSelectorOptions(false, this.filterQuery());
      }
    });

    effect(() => {
      if (this.selfField().valueDisplay !== null && this.selfField().valueDisplay !== undefined) {
        this.filterQuery.set(Array.isArray(this.selfField().valueDisplay) ? this.filterQuery() : this.selfField().valueDisplay as string);
      } else {
        this.filterQuery.set('');
      }
    });

    effect(() => {
      const value = this.selfField().value;
      if (value !== null && value !== undefined && value !== '') {
        this.unselectedLiteralHint = false;
      }
    });
  }

  backdropClick() {
    this.closeDropdown();
  }

  overlayDetached() {
    // Adding an "in"/"not in" value updates the trigger and can detach the overlay.
    // Do not treat that as "close"; the panel should stay available for more values.
    if (this.isMultiValueOperator) {
      return;
    }
    // valueSelected already closed the overlay. Do not run leave-logic on that
    // detach: selfField is still the pre-select object until the next CD, so
    // committing typed filter text would overwrite the chosen suggestion.
    if (!this.dropdownOpen) {
      return;
    }
    this.closeDropdown();
  }

  onValueBlur() {
    if (!this.hasDropdown || this.dropdownOpen || this.isMultiValueOperator) {
      return;
    }
    this.closeDropdown();
  }

  visibleFieldText(query: string): string {
    return visibleNameWithoutInvalidMarkers(query);
  }

  get unselectedHintText(): string {
    const key = 'TRANS__ADVANCED_SEARCH__QUERY_BUILDER_VALUE__NOT_SELECTED_FROM_LIST';
    const translated = this.translate.instant(key);
    return translated === key ? 'Ikke valgt fra listen' : translated;
  }

  get isMultiValueOperator(): boolean {
    return this.selfField().operator_selected === 'in' || this.selfField().operator_selected === 'not in';
  }

  get arraySelectedValues(): string {
    if (Array.isArray(this.selfField().valueDisplay) && this.selfField().valueDisplay.length) {
      return (this.selfField().valueDisplay as string[]).join(', ');
    }
    return '';
  }

  get inputType(): string {
    if ([FieldInputType.DATE_ISO.toString(),
      FieldInputType.DATE_TIME_ISO.toString(),
      FieldInputType.TEXT_AREA.toString()
    ].includes(this.selfField().input_type)) {
      return 'text';
    }

    if (this.selfField().input_type === 'number') {
      return 'number';
    }

    // Default is text as this is what is used for dropdown
    return 'text'
  }

  get isRange(): boolean {
    return this.selfField().operator_selected === 'range' || this.selfField().operator_selected === 'not range';
  }

  get hasDropdown(): boolean {
    return ['=', '<>', 'in', 'not in', 'in path'].includes(this.selfField().operator_selected) && [
      FieldInputType.MAP_ID.toString(),
      FieldInputType.REF_ARRAY.toString(),
      FieldInputType.SEARCH_SELECTOR_MULTIPLE.toString(),
      FieldInputType.SEARCH_SELECTOR.toString(),
      FieldInputType.DATE_ISO.toString(),
      FieldInputType.TEXT_AREA.toString(),
      FieldInputType.INPUT.toString()].includes(this.selfField().input_type);
  }

  get hasValue(): boolean {
    return this.filterQuery() !== '' || !!this.selfField().valueDisplay;
  }

  get ongoingSearch() {
    return localStorage.getItem('primus.searchOngoing') === 'true';
  }

  arrayValueSelected(): void {
    this.dropdownOpen = true;
  }

  clearValueInput(event: MouseEvent): void {
    event.stopPropagation();

    this.filterQuery.set('');
    this.unselectedLiteralHint = false;
    this.extendedSearchService.clearSelectedValue(this.selfField());
  }

  filterInput(event: Event) {
    if (this.filterInputDebounce !== null) {
      clearTimeout(this.filterInputDebounce);
    }

    this.filterInputDebounce = setTimeout(() => {
      // @ts-expect-error value DOES exist on target
      this.filterQuery.set(event.target.value);

      if (!this.hasDropdown) {
        // @ts-expect-error value DOES exist on target
        this.extendedSearchService.setSelectedFieldValueFromString(this.selfField(), event.target.value);
      }
    }, 500);
  }

  focusFirstNodeInDropdown(event: any) {
    event.preventDefault();

    if (this.dropdownOpen) {
      // @ts-ignore
      document.querySelectorAll('.value-tree__node:not(.value-tree__node--hidden) > .value-tree__node-button')[0].focus();
      // Select the children with class value-tree__node-button of every element WITH class value-tree__node and WITHOUT .value-tree__node--hidden
      // then focus the first of the resulting array
    } else {
      this.dropdownOpen = true;

      setTimeout(() => {
        // @ts-ignore
        document.querySelectorAll('.value-tree__node:not(.value-tree__node--hidden) > .value-tree__node-button')[0].focus();
        // Select the children with class value-tree__node-button of every element WITH class value-tree__node and WITHOUT .value-tree__node--hidden
        // then focus the first of the resulting array
      }, 100);
    }
  }

  getInternalDisplayValue(index: number) {
    if (!Array.isArray(this.selfField().value)) {
      return '';
    }

    return this.selfField().value[index];
  }

  setInternalValue(firstField?: any, secondField?: any) {
    if (Array.isArray(this.selfField().value)) {
      if (firstField) {
        this.extendedSearchService.setSelectedFieldValueFromArray(this.selfField(), [firstField.target.value, this.selfField().value[1]]);
      }

      if (secondField) {
        this.extendedSearchService.setSelectedFieldValueFromArray(this.selfField(), [this.selfField().value[0], secondField.target.value]);
      }
    } else {
      this.extendedSearchService.setSelectedFieldValueFromArray(this.selfField(), [firstField ? firstField.target.value : null, secondField ? secondField.target.value : null]);
    }
  }

  valueSelected(name: string) {
    if (this.isMultiValueOperator) {
      return;
    }
    this.filterQuery.set(name);
    this.unselectedLiteralHint = false;
    this.dropdownOpen = false;
  }

  private closeDropdown() {
    const result = resolveFieldValueOnLeave({
      operator: this.selfField().operator_selected,
      inputType: this.selfField().input_type,
      typedText: this.readLiveInputValue(),
      value: this.selfField().value,
      valueDisplay: this.selfField().valueDisplay,
    });

    if (result.commitTypedText !== null) {
      this.extendedSearchService.setFieldValueAndDisplay(
        this.selfField(),
        result.storedValue === '' ? null : result.storedValue,
        result.commitTypedText === '' ? null : result.commitTypedText,
      );
    }

    this.filterQuery.set(result.nextFilterQuery);
    this.unselectedLiteralHint = result.showUnselectedHint;
    this.dropdownOpen = false;
  }

  private readLiveInputValue(): string {
    const input = document.getElementById(this.valueInputId) as HTMLInputElement | null;
    if (input && typeof input.value === 'string') {
      return input.value;
    }
    return this.filterQuery();
  }

  private getValueSelectorOptions(clearValues: boolean = false, query: string = '') {
    if (clearValues) {
      this.values.set([]);
      return;
    }

    // if (!this.selfField().reference_id) {
    //   return;
    // }

    const path = this.selfField().path ?? this.selfField().field_name;

    if (!path) {
      return;
    }

    let superObjectTypeIds = [];
    const relationInfoFromGroup = this.extendedSearchService.getRelationInfoFromGroup(this.parentGroup(), this.rootGroup());

    if (relationInfoFromGroup?.superobjectTypeId) {
      superObjectTypeIds.push(relationInfoFromGroup.superobjectTypeId);
    } else {
      superObjectTypeIds = this.extendedSearchService.extendedSearchParameters().superobject_type_ids;
    }

    let folderId = this.searchContainer().getTargetId();

    if (folderId === 'none') {
      folderId = '';
    }

    this.extendedSearchService.getExtendedSearchFieldValues(this.selfField(), query, superObjectTypeIds, 1000, folderId).then((values) => {
      // P10-1650: keep backend relevance order when the user typed a keyword; A–Z only for empty browse.
      this.values.set(query.trim() ? values : this.sortNodes(values));
    })
  }

  private sortNodes(nodes) {
    for (const node of nodes) {
      if (node.children?.length) {
        node.children = this.sortNodes(node.children);
      }
    }

    return nodes.sort(this.naturalSort);
  }

  private readonly naturalSort = (a, b) => {
    return new Intl.Collator('nb-NO', {numeric: true, sensitivity: 'base'}).compare(a.$$name, b.$$name);
  }
}
